import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import * as chatApi from "@/api/endpoints/chat"

export const chatKeys = {
  all: ["chat"] as const,
  conversations: (businessId?: string) =>
    [...chatKeys.all, "conversations", businessId ?? "customer"] as const,
  messages: (conversationId: string) =>
    [...chatKeys.all, "messages", conversationId] as const,
}

export const useGetConversationsQuery = (
  businessId?: string,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: chatKeys.conversations(businessId),
    queryFn: () =>
      chatApi.getConversations({
        businessId,
        page: 1,
        pageSize: 50,
      }),
    enabled: options?.enabled ?? true,
  })

export const useGetMessagesQuery = (
  conversationId?: string,
  options?: { enabled?: boolean; page?: number; pageSize?: number },
) =>
  useQuery({
    queryKey: [...chatKeys.messages(conversationId ?? ""), options?.page ?? 1],
    queryFn: () =>
      chatApi.getMessages(conversationId!, {
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? 50,
      }),
    enabled: !!conversationId && (options?.enabled ?? true),
  })

export const useSendMessageMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      conversationId,
      content,
    }: {
      conversationId: string
      content: string
    }) => chatApi.sendMessage(conversationId, content),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: chatKeys.messages(variables.conversationId),
      })
      queryClient.invalidateQueries({ queryKey: chatKeys.all })
    },
  })
}

export const useMarkConversationReadMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: chatApi.markConversationRead,
    onSuccess: () => {
      // Refresh inbox unread badges only — avoid refetching the open thread.
      queryClient.invalidateQueries({
        queryKey: [...chatKeys.all, "conversations"],
      })
    },
  })
}
